import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Phone, Mail, CheckCircle2, Check, CreditCard, Loader2, Banknote, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentModal = ({ 
  isOpen, 
  customer, 
  onClose, 
  onConfirm, 
  onNavigateToCustomer,
  applyVat = (val) => val,
  isProcessing = false 
}) => {
  const [paymentTab, setPaymentTab] = useState('record');
  const [paymentAmountOption, setPaymentAmountOption] = useState('total');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paymentAccount, setPaymentAccount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [tipMethod, setTipMethod] = useState('cash_tip');
  const [creditOption, setCreditOption] = useState('credit_note');
  const [cardPaymentMethod, setCardPaymentMethod] = useState('stripe');
  const [success, setSuccess] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  useEffect(() => {
    if (isOpen && customer) {
      setPaymentAmount(customer.balance?.toFixed(2) || '0.00');
      setPaymentAmountOption('total');
      setCustomAmount('');
      setPaymentMethod('cash');
      setPaymentTab('record');
      setPaymentAccount('');
      setPaymentNotes('');
      setTipAmount('');
      setTipMethod('cash_tip');
      setCreditOption('credit_note');
      setCardPaymentMethod('stripe');
      setSuccess(false);
      setSendReceipt(true);
      setInvoices([]);
      setSelectedInvoiceIds(new Set());
      // Load outstanding invoices for the picker
      (async () => {
        setLoadingInvoices(true);
        try {
          const res = await fetch(`${API_URL}/api/payments/account/${customer.id}`);
          if (res.ok) {
            const d = await res.json();
            const list = d.invoices || [];
            setInvoices(list);
            setSelectedInvoiceIds(new Set(list.map(i => i.id)));
          }
        } catch (e) { /* picker unavailable, amount options still work */ }
        setLoadingInvoices(false);
      })();
    }
  }, [isOpen, customer]);
  
  if (!isOpen || !customer) return null;
  
  const invoicesSum = (ids) => invoices
    .filter(i => ids.has(i.id))
    .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);

  const toggleInvoice = (id) => {
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setPaymentAmount(invoicesSum(next).toFixed(2));
      return next;
    });
  };

  const handleConfirm = async () => {
    const data = {
      tab: paymentTab,
      amount: paymentTab === 'tips' ? tipAmount : paymentAmount,
      method: paymentTab === 'record' ? paymentMethod : 
              paymentTab === 'card' ? cardPaymentMethod :
              paymentTab === 'credit' ? creditOption : tipMethod,
      account: paymentAccount,
      notes: paymentNotes,
      customerId: customer.id,
      sendReceipt: paymentTab === 'record' && sendReceipt && !!customer.email,
      invoiceIds: paymentTab === 'record' && paymentAmountOption === 'unpaid'
        ? Array.from(selectedInvoiceIds) : null
    };
    
    const result = await onConfirm(data);
    if (result) {
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  };
  
  const currentAmount = paymentTab === 'tips' ? tipAmount : paymentAmount;
  const isValidAmount = currentAmount && parseFloat(currentAmount) > 0;
  
  return (
    <div 
      className="fixed inset-0 bg-zinc-900/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-100 rounded-3xl shadow-2xl w-full max-w-md modal-shell-h overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Clean Minimal Header */}
        <div className="p-4 flex items-center justify-between bg-white border-b border-zinc-200">
          <button 
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 rounded-xl transition-colors"
          >
            <X size={18} className="text-zinc-600" />
          </button>
          <h3 className="text-base font-bold text-zinc-900">Record Payment</h3>
          <div className="w-9" />
        </div>
        
        {success ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4 animate-bounce">
              <CheckCircle2 size={40} className="text-emerald-500" />
            </div>
            <h4 className="text-xl font-bold text-zinc-900 mb-1">Done!</h4>
            <p className="text-zinc-500 text-sm">Payment recorded successfully</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Customer Card - Clickable */}
            <div className="p-4">
              <button
                onClick={() => {
                  onClose();
                  onNavigateToCustomer && onNavigateToCustomer(customer);
                }}
                className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-lg transition-all group border border-zinc-200"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {customer.name?.charAt(0) || 'C'}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-bold text-zinc-900 truncate flex items-center gap-1">
                      {customer.name}
                      <ChevronRight size={18} className="text-emerald-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </p>
                    <p className="text-sm text-zinc-500 truncate">{customer.address}</p>
                  </div>
                  <div className="text-right pl-2">
                    <p className="text-2xl font-black text-red-500">£{customer.balance?.toFixed(2)}</p>
                    <p className="text-[10px] text-zinc-400 uppercase font-medium">Owed</p>
                  </div>
                </div>
              </button>
            </div>
            
            {/* Pill Tabs */}
            <div className="px-4 pb-3">
              <div className="flex gap-1.5 p-1 bg-white rounded-2xl shadow-sm">
                {[
                  { id: 'record', label: 'Record', emoji: '✏️' },
                  { id: 'card', label: 'Card', emoji: '💳' },
                  { id: 'credit', label: 'Credit', emoji: '🔄' },
                  { id: 'tips', label: 'Tips', emoji: '💰' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setPaymentTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      paymentTab === tab.id 
                        ? 'bg-zinc-900 text-white shadow-md' 
                        : 'text-zinc-500 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-sm">{tab.emoji}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="px-4 space-y-3 pb-4">
              {/* Amount Selection */}
              {paymentTab !== 'tips' ? (
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Select Amount</p>
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => { setPaymentAmountOption('total'); setPaymentAmount(customer.balance?.toFixed(2)); }}
                      className={`flex-1 py-3 rounded-xl text-center transition-all ${
                        paymentAmountOption === 'total' 
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' 
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      <p className="font-bold text-lg">£{customer.balance?.toFixed(2)}</p>
                      <p className="text-[10px] uppercase opacity-80">Full Balance</p>
                    </button>
                    <button
                      onClick={() => { setPaymentAmountOption('unpaid'); setPaymentAmount(invoicesSum(selectedInvoiceIds).toFixed(2)); }}
                      data-testid="amount-option-invoices"
                      className={`flex-1 py-3 rounded-xl text-center transition-all ${
                        paymentAmountOption === 'unpaid' 
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' 
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      <p className="font-bold text-lg">📋</p>
                      <p className="text-[10px] uppercase opacity-80">Invoices</p>
                    </button>
                    <button
                      onClick={() => setPaymentAmountOption('other')}
                      className={`flex-1 py-3 rounded-xl text-center transition-all ${
                        paymentAmountOption === 'other' 
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' 
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      <p className="font-bold text-lg">✎</p>
                      <p className="text-[10px] uppercase opacity-80">Custom</p>
                    </button>
                  </div>
                  {paymentAmountOption === 'other' && (
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 text-xl">£</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={customAmount}
                        onChange={(e) => { setCustomAmount(e.target.value); setPaymentAmount(e.target.value); }}
                        className="w-full pl-10 pr-4 py-3.5 bg-zinc-100 rounded-xl text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="0.00"
                        autoFocus
                      />
                    </div>
                  )}
                  {paymentAmountOption === 'unpaid' && (
                    <div className="space-y-2" data-testid="invoice-picker-list">
                      {loadingInvoices ? (
                        <div className="flex justify-center py-4">
                          <Loader2 size={20} className="animate-spin text-zinc-400" />
                        </div>
                      ) : invoices.length === 0 ? (
                        <p className="text-center text-sm text-zinc-400 py-3">No outstanding invoices found</p>
                      ) : (
                        <>
                          {invoices.map((inv) => (
                            <label
                              key={inv.id}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors border ${
                                selectedInvoiceIds.has(inv.id)
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : 'bg-zinc-50 border-transparent opacity-60'
                              }`}
                              data-testid={`invoice-pick-${inv.invoice_number}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedInvoiceIds.has(inv.id)}
                                onChange={() => toggleInvoice(inv.id)}
                                className="w-4 h-4 accent-emerald-600"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-zinc-900">{inv.invoice_number}</p>
                                <p className="text-xs text-zinc-400 truncate">
                                  {inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                  {inv.notes ? ` — ${inv.notes}` : ''}
                                </p>
                              </div>
                              <span className="text-sm font-bold text-zinc-900">£{(parseFloat(inv.total) || 0).toFixed(2)}</span>
                            </label>
                          ))}
                          <div className="flex items-center justify-between px-1 pt-1">
                            <span className="text-xs text-zinc-400">{selectedInvoiceIds.size} of {invoices.length} selected</span>
                            <span className="text-sm font-bold text-emerald-600">£{invoicesSum(selectedInvoiceIds).toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-4 shadow-sm">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">Tip Amount</p>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 text-xl font-bold">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value)}
                      className="w-full pl-10 pr-4 py-4 bg-emerald-50 rounded-xl text-3xl font-bold text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
              
              {/* Payment Methods */}
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">
                  {paymentTab === 'record' ? 'Payment Method' : paymentTab === 'card' ? 'Card Provider' : paymentTab === 'credit' ? 'Credit Type' : 'Tip Method'}
                </p>
                
                {paymentTab === 'record' && (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'cash', label: 'Cash', icon: '💵' },
                      { id: 'cheque', label: 'Cheque', icon: '📝' },
                      { id: 'bank_transfer', label: 'Bank', icon: '🏦' },
                      { id: 'manual_card', label: 'Card', icon: '💳' },
                      { id: 'other', label: 'Other', icon: '📋' },
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setPaymentMethod(m.id)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          paymentMethod === m.id 
                            ? 'bg-zinc-900 text-white shadow-md' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                        }`}
                      >
                        <span className="text-xl block mb-1">{m.icon}</span>
                        <span className="text-[10px] font-medium">{m.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {paymentTab === 'card' && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setCardPaymentMethod('gocardless')}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                        cardPaymentMethod === 'gocardless' 
                          ? 'bg-yellow-50 ring-2 ring-yellow-400' 
                          : 'bg-zinc-100 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="w-12 h-12 bg-yellow-400 rounded-2xl flex items-center justify-center text-yellow-900 font-black text-lg shadow-lg shadow-yellow-200">G</div>
                      <div className="text-left flex-1">
                        <p className="font-bold text-zinc-900">GoCardless</p>
                        <p className="text-xs text-zinc-500">Direct Debit</p>
                      </div>
                      {cardPaymentMethod === 'gocardless' && <Check size={20} className="text-yellow-600" />}
                    </button>
                    
                    <button
                      onClick={() => setCardPaymentMethod('stripe')}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all ${
                        cardPaymentMethod === 'stripe' 
                          ? 'bg-indigo-50 ring-2 ring-indigo-400' 
                          : 'bg-zinc-100 hover:bg-zinc-200'
                      }`}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-black text-lg shadow-lg shadow-indigo-200">S</div>
                      <div className="text-left flex-1">
                        <p className="font-bold text-zinc-900">Stripe</p>
                        <p className="text-xs text-zinc-500">Card •••• {customer.phone?.slice(-4) || '0000'}</p>
                      </div>
                      {cardPaymentMethod === 'stripe' && <Check size={20} className="text-indigo-600" />}
                    </button>
                  </div>
                )}
                
                {paymentTab === 'credit' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCreditOption('credit_note')}
                      className={`flex-1 p-4 rounded-xl text-center transition-all ${
                        creditOption === 'credit_note' 
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-200' 
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      <span className="text-3xl block mb-2">📄</span>
                      <span className="font-bold text-sm">Credit Note</span>
                    </button>
                    <button
                      onClick={() => setCreditOption('write_off')}
                      className={`flex-1 p-4 rounded-xl text-center transition-all ${
                        creditOption === 'write_off' 
                          ? 'bg-red-500 text-white shadow-lg shadow-red-200' 
                          : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                      }`}
                    >
                      <span className="text-3xl block mb-2">✖️</span>
                      <span className="font-bold text-sm">Write Off</span>
                    </button>
                  </div>
                )}
                
                {paymentTab === 'tips' && (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'cash_tip', label: 'Cash', icon: '💵' },
                      { id: 'tip_cheque', label: 'Cheque', icon: '📝' },
                      { id: 'tip_bank', label: 'Bank', icon: '🏦' },
                      { id: 'tip_card', label: 'Card', icon: '💳' },
                      { id: 'tip_other', label: 'Other', icon: '📋' },
                    ].map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setTipMethod(m.id)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          tipMethod === m.id 
                            ? 'bg-zinc-900 text-white shadow-md' 
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                        }`}
                      >
                        <span className="text-xl block mb-1">{m.icon}</span>
                        <span className="text-[10px] font-medium">{m.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Reference & Date */}
              <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
                {(paymentTab === 'record' || paymentTab === 'tips') && (
                  <input
                    type="text"
                    value={paymentAccount}
                    onChange={(e) => setPaymentAccount(e.target.value)}
                    placeholder="Account"
                    className="w-full px-4 py-3 bg-zinc-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                )}
                <input
                  type="text"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Reference (optional)"
                  className="w-full px-4 py-3 bg-zinc-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                <div className="flex items-center justify-between text-sm px-1">
                  <span className="text-zinc-400">Date</span>
                  <span className="font-semibold text-zinc-900">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                </div>
              </div>

              {paymentTab === 'record' && customer.email && (
                <label className="flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendReceipt}
                    onChange={(e) => setSendReceipt(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="owings-send-receipt-checkbox"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-zinc-900">Email receipt</p>
                    <p className="text-xs text-zinc-400 truncate">{customer.email}</p>
                  </div>
                </label>
              )}
            </div>
          </div>
        )}
        
        {/* Footer Button */}
        {!success && (
          <div className="p-4 bg-white border-t border-zinc-200">
            <button
              onClick={handleConfirm}
              disabled={isProcessing || !isValidAmount}
              className="w-full py-4 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-bold rounded-2xl transition-all active:scale-[0.98]"
            >
              {isProcessing ? (
                <Loader2 size={20} className="animate-spin mx-auto" />
              ) : isValidAmount ? (
                `${paymentTab === 'credit' && creditOption === 'write_off' ? 'Write Off' : paymentTab === 'tips' ? 'Record Tip' : paymentTab === 'card' ? 'Charge' : 'Record'} £${parseFloat(currentAmount).toFixed(2)}`
              ) : (
                'Enter Amount'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
